/**
 * The web spelling of the server's limit keys (services/server/boot/limits_registry.go).
 * Each value must be declared in Go as `<Name> limits.Key = "<value>"`; the
 * parity test in __tests__/limits.test.ts reads the Go source to prove it, so a
 * typo here fails a test instead of silently showing no counter.
 */
export const LIMIT_KEYS = {
  chatTitle: 'chat.title',
  // LIMIT-37: chat.SelectedTextLimitKey (internal/chat/selected_text_limit.go) and
  // websocket.OfflineQueueLimitKey (internal/websocket/offline_queue_limit.go).
  chatSelectedText: 'chat.selected_text',
  wsOfflineQueue: 'ws.offline_queue',
  workbatchLabel: 'workbatch.label',
  workbatchMovedOut: 'workbatch.moved_out',
  workbatchSplits: 'workbatch.splits',
  workbatchGroups: 'workbatch.groups',
  workbatchProposals: 'workbatch.proposals',
  // Declared in Go as dag.ConfidentialWaitMaxLimitKey (internal/engine/dag).
  researchConfidentialWaitMax: 'research.confidential_wait_max',
  nextworkNowMax: 'nextwork.now_max',
  nextworkLightMax: 'nextwork.light_max',
  cosActivityTtl: 'cos.activity_ttl_seconds',
  // COS-29 interruptions (internal/cos/interrupt/limits.go).
  cosInterruptBudgetMax: 'cos.interrupt_budget_max',
  cosInterruptBudgetDefault: 'cos.interrupt_budget_default',
  cosInterruptSnooze: 'cos.interrupt_snooze_seconds',
  cosTellMeMax: 'cos.tell_me_max',
  cosInterruptLogRetention: 'cos.interrupt_log_retention_seconds',
  cosInterruptPgRetention: 'cos.interrupt_pg_retention_seconds',
  cosInterruptSeenRetention: 'cos.interrupt_seen_retention_seconds',
  // HOME-03 focus sprints (internal/cos/focussprint/limits.go).
  cosSprintMaxMinutes: 'cos.sprint_max_minutes',
  cosSprintDefaultMinutes: 'cos.sprint_default_minutes',
  cosSprintBreakMinutes: 'cos.sprint_break_minutes',
  cosSprintBreakMaxMinutes: 'cos.sprint_break_max_minutes',
  cosSprintMaxSteps: 'cos.sprint_max_steps',
  cosSprintStepTitleChars: 'cos.sprint_step_title_chars',
  cosSprintGoalChars: 'cos.sprint_goal_chars',
  cosSprintDraftDaily: 'cos.sprint_draft_daily',
  cosSprintPgRetention: 'cos.sprint_pg_retention_seconds',
  // COS-26 attention suggestions (internal/tasks/attention_suggest.go).
  tasksAttentionSuggestPerPass: 'tasks.attention_suggest_per_pass',
  tasksAttentionSuggestDaily: 'tasks.attention_suggest_daily',
  // LIMIT-32 inbox intake caps (internal/inbox/intake/imap/limits.go, internal/inbox/contract/limitkeys.go,
  // internal/inbox/intake/telegram/retry/limitkeys.go). The card renders the numbers from each cut record and the
  // Telegram status, not from this registry; these are chat's and the operator's readout.
  inboxImportTextBytes: 'inbox.import_text_bytes',
  inboxImportParts: 'inbox.import_parts',
  inboxImportParticipants: 'inbox.import_participants',
  inboxImportHeaderBytes: 'inbox.import_header_bytes',
  inboxSubjectBytes: 'inbox.subject_bytes',
  inboxDisplayNameBytes: 'inbox.display_name_bytes',
  inboxTelegramRetryHorizon: 'inbox.telegram_retry_horizon',
  inboxTelegramRetryCap: 'inbox.telegram_retry_cap',
  // LIMIT-24 GDPR export cooldown (internal/export/limit_keys.go) and snapshot tag caps
  // (internal/workspace/limit_keys.go).
  gdprExportCooldown: 'gdpr.export.cooldown',
  workspaceSnapshotTags: 'workspace.snapshot.tags',
  workspaceSnapshotTagField: 'workspace.snapshot.tag_field',
  // LIMIT-34 task caps (internal/tasks/caps.go). tasks.links and tasks.writes_per_window have no web input, so
  // they are reported to chat through the registry only.
  tasksTitle: 'tasks.title',
  tasksNotes: 'tasks.notes',
  tasksTags: 'tasks.tags',
  tasksTag: 'tasks.tag',
  tasksLinkLabel: 'tasks.link_label',
  tasksEstimate: 'tasks.estimate',
  tasksSeries: 'tasks.series',
  tasksRecurrenceInterval: 'tasks.recurrence_interval',
  // LIMIT-08 SSO claim caps (internal/auth/providers/oidc/caps.go) and the background-authorization window
  // (internal/bgauth/config.go). bgauthMaxAge is read by Settings → Background execution; the rest are
  // chat's and the operator's readout.
  ssoMaxGroups: 'sso.max_groups',
  ssoMaxGroupBytes: 'sso.max_group_bytes',
  ssoDisplayNameMaxChars: 'sso.display_name_max_chars',
  bgauthMaxAge: 'bgauth.max_age',
  bgauthExpiryWarn: 'bgauth.expiry_warn',
} as const;
